/**
 * Drawn delivery areas (GeoJSON Polygon / MultiPolygon, coordinates [longitude, latitude]), same logic as
 * geo.service.js of the api: the polygon decides, the enclosing circle (Latitude, Longitude, Radius) is the fast
 * precheck and the display for older app versions.
 */
export interface AreaGeometry {
  type: 'Polygon' | 'MultiPolygon';
  coordinates: any;
}

type Ring = number[][];

function isRing(ring: any): boolean {
  return Array.isArray(ring) && ring.length >= 3 && ring.every(point => Array.isArray(point) && point.length >= 2
    && isFinite(Number(point[0])) && isFinite(Number(point[1])));
}

// polygons of the geometry (each: outline and holes), null for an invalid geometry
export function getPolygons(geometry: AreaGeometry): Ring[][] {
  if (!geometry || typeof geometry !== 'object') {
    return null;
  }
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : (geometry.type === 'MultiPolygon' ? geometry.coordinates : null);
  if (!Array.isArray(polygons) || polygons.length === 0 || !polygons.every(polygon => Array.isArray(polygon) && polygon.length > 0 && polygon.every(isRing))) {
    return null;
  }
  return polygons;
}

function inRing(x: number, y: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i][0]), yi = Number(ring[i][1]);
    const xj = Number(ring[j][0]), yj = Number(ring[j][1]);
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

// the point is inside of an outline and not in one of its holes
export function isInGeometry(geometry: AreaGeometry, latitude: number, longitude: number): boolean {
  const polygons = getPolygons(geometry);
  if (!polygons || latitude == null || longitude == null) {
    return false;
  }
  const x = Number(longitude);
  const y = Number(latitude);
  return polygons.some(([outline, ...holes]) => inRing(x, y, outline) && !holes.some(hole => inRing(x, y, hole)));
}

// distance in meters (haversine)
export function distanceInMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLng = (lng2 - lng1) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// enclosing circle: middle of the bounding box, distance to the farthest point (meters, rounded up)
export function getEnclosingCircle(geometry: AreaGeometry): { Latitude: number, Longitude: number, Radius: number } {
  const polygons = getPolygons(geometry);
  if (!polygons) {
    return null;
  }
  const points: Ring = [].concat(...polygons.map(polygon => polygon[0]));
  const lngs = points.map(point => Number(point[0]));
  const lats = points.map(point => Number(point[1]));
  const latitude = (Math.min(...lats) + Math.max(...lats)) / 2;
  const longitude = (Math.min(...lngs) + Math.max(...lngs)) / 2;
  const radius = Math.max(...points.map(point => distanceInMeters(latitude, longitude, Number(point[1]), Number(point[0]))));
  return {
    Latitude: Math.round(latitude * 1000000) / 1000000,
    Longitude: Math.round(longitude * 1000000) / 1000000,
    Radius: Math.ceil(radius) + 1
  };
}

// number of corners of the outlines (display)
export function countPoints(geometry: AreaGeometry): number {
  const polygons = getPolygons(geometry);
  return polygons ? polygons.reduce((sum, polygon) => sum + Math.max(polygon[0].length - 1, 0), 0) : 0;
}
